import React, { useMemo, useState } from "react";
import DebtDetailModal from "./DebtDetailModal";

function DebtOverview({ rows }) {
  const [filterBigDebt, setFilterBigDebt] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  const stats = useMemo(() => {
    const byVehicle = new Map();

    (rows || []).forEach((row) => {
      if (!row) return;
      const soXe = row.soXe || "Chưa có số xe";
      const laiXe = row.laiXe || "";

      const doanhThu =
        typeof row.doanhThu === "number"
          ? row.doanhThu
          : Number(String(row.doanhThu || "").replace(/[^\d-]/g, "")) || 0;

      const keToan =
        row.keToanChuyen === "" ||
        row.keToanChuyen === null ||
        row.keToanChuyen === undefined
          ? 0
          : typeof row.keToanChuyen === "number"
          ? row.keToanChuyen
          : Number(String(row.keToanChuyen).replace(/[^\d-]/g, "")) || 0;

      if (!doanhThu && !keToan) return;

      const key = soXe;
      if (!byVehicle.has(key)) {
        byVehicle.set(key, {
          soXe,
          drivers: new Set(),
          totalDoanhThu: 0,
          totalDaChuyen: 0,
        });
      }

      const item = byVehicle.get(key);
      if (laiXe) item.drivers.add(laiXe);
      item.totalDoanhThu += doanhThu;
      item.totalDaChuyen += keToan;
    });

    const list = Array.from(byVehicle.values()).map((v) => {
      const conNo = v.totalDoanhThu - v.totalDaChuyen; // Có thể âm (thừa) hoặc dương (nợ)
      return {
        soXe: v.soXe,
        drivers: Array.from(v.drivers).sort((a, b) =>
          a.localeCompare(b, "vi")
        ),
        totalDoanhThu: v.totalDoanhThu,
        totalDaChuyen: v.totalDaChuyen,
        conNo: conNo, // Có thể âm (thừa) hoặc dương (nợ)
      };
    });

    // Sắp xếp: xe nợ nhiều nhất lên trên
    list.sort((a, b) => b.conNo - a.conNo || a.soXe.localeCompare(b.soXe, "vi"));
    return list;
  }, [rows]);

  const filteredStats = useMemo(() => {
    if (!filterBigDebt) return stats;
    return stats.filter((v) => v.conNo >= 2000000);
  }, [stats, filterBigDebt]);

  // Summary tổng quan luôn tính trên toàn bộ stats (không phụ thuộc filter)
  const totalRevenue = useMemo(
    () => stats.reduce((sum, v) => sum + v.totalDoanhThu, 0),
    [stats]
  );
  const totalDebt = useMemo(
    () => stats.reduce((sum, v) => sum + Math.max(v.conNo, 0), 0), // Chỉ tính tổng nợ (số dương)
    [stats]
  );

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <div className="mb-5">
        <h1 className="text-2xl font-semibold text-slate-900 mb-1">
          Công nợ theo xe
        </h1>
        <p className="text-sm text-slate-600">
          Kế toán xem nhanh doanh thu và số tiền còn nợ của từng xe. Xe nợ từ{" "}
          <span className="font-semibold text-red-600">2.000.000 đ</span> trở lên
          sẽ được tô đỏ để dễ theo dõi.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-3 sm:p-4">
          <p className="text-xs text-slate-500 mb-1">Tổng doanh thu</p>
          <p className="text-lg sm:text-xl font-semibold text-slate-900 break-words">
            {totalRevenue.toLocaleString("vi-VN")} đ
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-3 sm:p-4">
          <p className="text-xs text-slate-500 mb-1">Tổng còn nợ</p>
          <p className="text-lg sm:text-xl font-semibold text-amber-700 break-words">
            {totalDebt.toLocaleString("vi-VN")} đ
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-3 sm:p-4 sm:col-span-2 md:col-span-1">
          <p className="text-xs text-slate-500 mb-1">Số xe đang nợ</p>
          <p className="text-lg sm:text-xl font-semibold text-slate-900">
            {stats.filter((v) => v.conNo > 0).length}
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="px-3 sm:px-4 py-2 sm:py-3 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <h2 className="text-sm sm:text-base font-semibold text-slate-900">
            Danh sách công nợ theo xe
          </h2>
          <div className="flex items-center gap-3 sm:gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filterBigDebt}
                onChange={(e) => setFilterBigDebt(e.target.checked)}
                className="w-4 h-4 text-red-600 border-slate-300 rounded focus:ring-red-500"
              />
              <span className="text-xs sm:text-sm text-slate-700">
                Chỉ hiển thị xe nợ ≥ 2 triệu
              </span>
            </label>
            <span className="text-xs text-slate-500">
              Hiển thị: {filteredStats.length} / {stats.length} xe
            </span>
          </div>
        </div>
        <div className="overflow-x-auto -mx-3 sm:mx-0">
          <div className="inline-block min-w-full align-middle">
            <table className="min-w-full divide-y divide-slate-200 text-xs sm:text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-2 sm:px-3 py-2 text-left text-xs font-semibold text-slate-600 whitespace-nowrap">
                    Số xe
                  </th>
                  <th className="px-2 sm:px-3 py-2 text-left text-xs font-semibold text-slate-600 whitespace-nowrap">
                    Lái xe
                  </th>
                  <th className="px-2 sm:px-3 py-2 text-right text-xs font-semibold text-slate-600 whitespace-nowrap">
                    Doanh thu
                  </th>
                  <th className="px-2 sm:px-3 py-2 text-right text-xs font-semibold text-slate-600 whitespace-nowrap">
                    Đã chuyển
                  </th>
                  <th className="px-2 sm:px-3 py-2 text-right text-xs font-semibold text-slate-600 whitespace-nowrap">
                    Nợ/Thừa
                  </th>
                  <th className="px-2 sm:px-3 py-2 text-center text-xs font-semibold text-slate-600 whitespace-nowrap">
                    Chi tiết
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {filteredStats.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-2 sm:px-3 py-4 text-center text-xs text-slate-500"
                    >
                      {filterBigDebt
                        ? "Không có xe nào nợ ≥ 2 triệu."
                        : "Chưa có dữ liệu."}
                    </td>
                  </tr>
                )}
                {filteredStats.map((item) => {
                  const isBigDebt = item.conNo >= 2000000;
                  const isThua = item.conNo < 0;
                  const isNo = item.conNo > 0;
                  return (
                    <tr key={item.soXe} className={`${isBigDebt ? "bg-red-50" : "bg-white"} hover:bg-slate-100`}>
                      <td className="px-2 sm:px-3 py-2 whitespace-nowrap text-xs font-semibold text-slate-900">
                        {item.soXe}
                      </td>
                      <td className="px-2 sm:px-3 py-2 whitespace-nowrap text-xs text-slate-700">
                        {item.drivers.length > 0 ? item.drivers.join(", ") : "—"}
                      </td>
                      <td className="px-2 sm:px-3 py-2 whitespace-nowrap text-xs text-right text-slate-700">
                        {item.totalDoanhThu.toLocaleString("vi-VN")} đ
                      </td>
                      <td className="px-2 sm:px-3 py-2 whitespace-nowrap text-xs text-right text-slate-700">
                        {item.totalDaChuyen.toLocaleString("vi-VN")} đ
                      </td>
                      <td
                        className={`px-2 sm:px-3 py-2 whitespace-nowrap text-xs text-right font-semibold ${
                          isBigDebt
                            ? "text-red-700"
                            : isNo
                            ? "text-amber-700"
                            : isThua
                            ? "text-blue-700"
                            : "text-emerald-700"
                        }`}
                      >
                        {isNo
                          ? `Nợ ${Math.abs(item.conNo).toLocaleString("vi-VN")} đ`
                          : isThua
                          ? `Thừa ${Math.abs(item.conNo).toLocaleString("vi-VN")} đ`
                          : "0 đ"}
                      </td>
                      <td className="px-2 sm:px-3 py-2 whitespace-nowrap text-center">
                        <button
                          onClick={() => setSelectedVehicle(item.soXe)}
                          className="text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          Xem
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {selectedVehicle && (
        <DebtDetailModal
          vehicleNumber={selectedVehicle}
          rows={rows}
          onClose={() => setSelectedVehicle(null)}
        />
      )}
    </div>
  );
}

export default DebtOverview;

